import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { templatesApi } from '../services/api';
import { formatApiError } from '../utils/helpers';
import { buildTicketPrintHTML } from '../utils/ticketRenderer';
import JsBarcode from 'jsbarcode';
import { CaretUp, CaretDown, Eye, EyeSlash, ArrowCounterClockwise, FloppyDisk, Printer, ClockCounterClockwise, Trash, Plus, Star } from '@phosphor-icons/react';

// ============================================================
// AdminTicketDesigner
// Structured block-based editor for entry & exit ticket templates.
// The Live Preview and Test Print use the SAME buildTicketPrintHTML that
// production tickets use, so what you see is what prints.
// This module does NOT alter the print delivery mechanism.
// ============================================================

const BLOCK_TYPE_LABELS = {
  logo: 'Logo',
  business_name: 'Nombre del negocio',
  address: 'Dirección',
  separator: 'Separador',
  ticket_id: 'ID de ticket',
  plate: 'Patente',
  space: 'Espacio',
  vehicle_type: 'Tipo de vehículo',
  entry_date: 'Fecha entrada',
  exit_date: 'Fecha salida',
  duration: 'Duración',
  charges_detail: 'Detalle de cargos',
  products: 'Productos',
  services: 'Servicios',
  bathrooms: 'Baños',
  subtotal: 'Subtotal',
  total: 'Total',
  payment_method: 'Método de pago',
  barcode: 'Código de barras / QR',
  phone: 'Teléfono',
  website: 'Sitio web',
  social: 'Redes sociales',
  footer_message: 'Mensaje al pie',
};

const AVAILABLE_BLOCK_TYPES = Object.keys(BLOCK_TYPE_LABELS);

const SIZE_OPTIONS = [
  { value: 'xs', label: 'Muy pequeño' },
  { value: 'small', label: 'Pequeño' },
  { value: 'medium', label: 'Mediano' },
  { value: 'large', label: 'Grande' },
  { value: 'xl', label: 'Muy grande' },
];

const STYLE_OPTIONS = [
  { value: 'normal', label: 'Normal' },
  { value: 'bold', label: 'Negrita' },
  { value: 'centered', label: 'Centrado' },
  { value: 'bold_centered', label: 'Negrita + Centrado' },
];

const DIVIDER_OPTIONS = [
  { value: 'none', label: 'Sin línea' },
  { value: 'thin', label: 'Línea fina' },
  { value: 'dashed', label: 'Línea punteada' },
];

const SAMPLE_ENTRY = {
  ticket_id: 'TK-DEMO-001',
  plate_number: 'ABCD12',
  space_label: 'A5',
  vehicle_type: 'Auto',
  entry_time: new Date().toISOString(),
  business_name: 'ESTACIONAMIENTO GLANDYMAR',
};

const SAMPLE_EXIT = {
  ...SAMPLE_ENTRY,
  ticket_id: 'TK-DEMO-002',
  exit_time: new Date().toISOString(),
  total_minutes: 95,
  parking_amount: 3000,
  total_price: 4500,
  payment_method: 'cash',
  cash_received: 5000,
  cash_change: 500,
  breakdown: [
    { description: 'Estacionamiento (95 min)', amount: 3000 },
    { description: 'Baño x2', amount: 1000 },
    { description: 'Agua', amount: 500 },
  ],
  extras_breakdown: [
    { item_name: 'Baño', category: 'service', quantity: 2, line_total: 1000 },
    { item_name: 'Agua', category: 'product', quantity: 1, line_total: 500 },
  ],
};

const uid = () => 'b_' + Math.random().toString(36).slice(2, 8);

function BlockRow({ block, selected, onSelect, onMoveUp, onMoveDown, onToggleVisible, onRemove, canUp, canDown }) {
  return (
    <div
      onClick={onSelect}
      className={`flex items-center gap-2 p-2 rounded-lg border cursor-pointer transition-colors ${selected ? 'bg-[#052E16] border-[#16A34A]' : 'bg-[#1C1C1E] border-[#27272A] hover:border-[#3F3F46]'}`}
      data-testid={`block-row-${block.type}`}
    >
      <div className="flex flex-col gap-0.5">
        <button
          onClick={(e) => { e.stopPropagation(); onMoveUp(); }}
          disabled={!canUp}
          className="p-0.5 text-[#A1A1AA] hover:text-white disabled:opacity-20"
          data-testid={`block-up-${block.type}`}
        ><CaretUp className="w-3 h-3" weight="bold" /></button>
        <button
          onClick={(e) => { e.stopPropagation(); onMoveDown(); }}
          disabled={!canDown}
          className="p-0.5 text-[#A1A1AA] hover:text-white disabled:opacity-20"
          data-testid={`block-down-${block.type}`}
        ><CaretDown className="w-3 h-3" weight="bold" /></button>
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5">
          <span className={`text-sm font-medium ${block.visible ? 'text-white' : 'text-[#71717A] line-through'}`}>
            {BLOCK_TYPE_LABELS[block.type] || block.type}
          </span>
          {block.highlight && <Star className="w-3 h-3 text-[#FCD34D]" weight="fill" />}
        </div>
        {block.label && (
          <p className="text-[10px] text-[#71717A] truncate">"{block.label}"</p>
        )}
      </div>
      <button
        onClick={(e) => { e.stopPropagation(); onToggleVisible(); }}
        className="p-1 text-[#A1A1AA] hover:text-white"
        title={block.visible ? 'Ocultar' : 'Mostrar'}
        data-testid={`block-toggle-${block.type}`}
      >
        {block.visible ? <Eye className="w-4 h-4" weight="bold" /> : <EyeSlash className="w-4 h-4" weight="bold" />}
      </button>
      <button
        onClick={(e) => { e.stopPropagation(); onRemove(); }}
        className="p-1 text-[#71717A] hover:text-[#F87171]"
        title="Eliminar bloque"
        data-testid={`block-remove-${block.type}`}
      >
        <Trash className="w-3.5 h-3.5" weight="bold" />
      </button>
    </div>
  );
}

function BlockProperties({ block, onChange }) {
  if (!block) {
    return (
      <div className="text-center text-[#71717A] text-sm py-8 border border-dashed border-[#27272A] rounded-lg">
        Selecciona un bloque para editarlo
      </div>
    );
  }
  const upd = (patch) => onChange({ ...block, ...patch });
  const showLabel = !['logo', 'separator', 'barcode'].includes(block.type);
  const showHighlight = ['plate', 'space', 'total', 'ticket_id'].includes(block.type);
  return (
    <div className="space-y-3" data-testid="block-properties">
      <div>
        <label className="text-[#A1A1AA] text-xs uppercase tracking-wide block mb-1">Tipo</label>
        <div className="px-3 py-2 bg-[#1C1C1E] border border-[#27272A] rounded text-white text-sm">{BLOCK_TYPE_LABELS[block.type]}</div>
      </div>
      {showLabel && (
        <div>
          <label className="text-[#A1A1AA] text-xs uppercase tracking-wide block mb-1">Etiqueta (dejar vacía para ocultar)</label>
          <input
            value={block.label || ''}
            onChange={(e) => upd({ label: e.target.value })}
            placeholder="Ej: PATENTE, PLACA, VEHICLE..."
            className="w-full px-3 py-2 bg-[#1C1C1E] border border-[#27272A] rounded text-white text-sm focus:border-[#16A34A] outline-none"
            data-testid="block-label-input"
          />
        </div>
      )}
      <div className="grid grid-cols-2 gap-2">
        <div>
          <label className="text-[#A1A1AA] text-xs uppercase tracking-wide block mb-1">Tamaño</label>
          <select value={block.size} onChange={(e) => upd({ size: e.target.value })}
            className="w-full px-2 py-2 bg-[#1C1C1E] border border-[#27272A] rounded text-white text-sm focus:border-[#16A34A] outline-none"
            data-testid="block-size-select">
            {SIZE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </div>
        <div>
          <label className="text-[#A1A1AA] text-xs uppercase tracking-wide block mb-1">Estilo</label>
          <select value={block.style} onChange={(e) => upd({ style: e.target.value })}
            className="w-full px-2 py-2 bg-[#1C1C1E] border border-[#27272A] rounded text-white text-sm focus:border-[#16A34A] outline-none"
            data-testid="block-style-select">
            {STYLE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div>
          <label className="text-[#A1A1AA] text-xs uppercase tracking-wide block mb-1">Margen arriba</label>
          <input type="number" min="0" max="30" value={block.margin_top || 0} onChange={(e) => upd({ margin_top: Number(e.target.value) || 0 })}
            className="w-full px-2 py-2 bg-[#1C1C1E] border border-[#27272A] rounded text-white text-sm focus:border-[#16A34A] outline-none"
            data-testid="block-mt-input" />
        </div>
        <div>
          <label className="text-[#A1A1AA] text-xs uppercase tracking-wide block mb-1">Margen abajo</label>
          <input type="number" min="0" max="30" value={block.margin_bottom || 0} onChange={(e) => upd({ margin_bottom: Number(e.target.value) || 0 })}
            className="w-full px-2 py-2 bg-[#1C1C1E] border border-[#27272A] rounded text-white text-sm focus:border-[#16A34A] outline-none"
            data-testid="block-mb-input" />
        </div>
      </div>
      <div>
        <label className="text-[#A1A1AA] text-xs uppercase tracking-wide block mb-1">Divisor debajo</label>
        <select value={block.divider} onChange={(e) => upd({ divider: e.target.value })}
          className="w-full px-2 py-2 bg-[#1C1C1E] border border-[#27272A] rounded text-white text-sm focus:border-[#16A34A] outline-none"
          data-testid="block-divider-select">
          {DIVIDER_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      </div>
      {showHighlight && (
        <label className="flex items-center gap-2 cursor-pointer p-2 bg-[#422006]/40 border border-[#D97706]/40 rounded" data-testid="block-highlight-toggle">
          <input type="checkbox" checked={!!block.highlight} onChange={(e) => upd({ highlight: e.target.checked })} className="accent-[#FCD34D]" />
          <div>
            <div className="text-[#FCD34D] text-sm font-medium flex items-center gap-1"><Star className="w-3.5 h-3.5" weight="fill" />Destacar</div>
            <div className="text-[#A1A1AA] text-xs">Imprime este bloque en tamaño gigante</div>
          </div>
        </label>
      )}
    </div>
  );
}

function HeaderFooterEditor({ header, footer, barcodeMode, onHeaderChange, onFooterChange, onBarcodeModeChange }) {
  return (
    <div className="space-y-3" data-testid="header-footer-editor">
      <details className="bg-[#1C1C1E] border border-[#27272A] rounded-lg" open>
        <summary className="p-2.5 text-white text-sm font-medium cursor-pointer">Encabezado (Header)</summary>
        <div className="p-3 pt-0 space-y-2">
          <input value={header.business_name || ''} onChange={(e) => onHeaderChange({ ...header, business_name: e.target.value })}
            placeholder="Nombre del negocio (vacío = usa el ajuste global)"
            className="w-full px-3 py-2 bg-black border border-[#27272A] rounded text-white text-sm focus:border-[#16A34A] outline-none"
            data-testid="header-business-input" />
          <input value={header.address || ''} onChange={(e) => onHeaderChange({ ...header, address: e.target.value })}
            placeholder="Dirección"
            className="w-full px-3 py-2 bg-black border border-[#27272A] rounded text-white text-sm focus:border-[#16A34A] outline-none"
            data-testid="header-address-input" />
          <input value={header.logo_url || ''} onChange={(e) => onHeaderChange({ ...header, logo_url: e.target.value })}
            placeholder="URL del logo (opcional)"
            className="w-full px-3 py-2 bg-black border border-[#27272A] rounded text-white text-sm focus:border-[#16A34A] outline-none"
            data-testid="header-logo-input" />
          <label className="flex items-center gap-2 text-white text-sm">
            <input type="checkbox" checked={!!header.show_logo} onChange={(e) => onHeaderChange({ ...header, show_logo: e.target.checked })} className="accent-[#16A34A]" data-testid="header-showlogo-toggle" />
            Mostrar logo
          </label>
        </div>
      </details>
      <details className="bg-[#1C1C1E] border border-[#27272A] rounded-lg">
        <summary className="p-2.5 text-white text-sm font-medium cursor-pointer">Pie (Footer)</summary>
        <div className="p-3 pt-0 space-y-2">
          <input value={footer.phone || ''} onChange={(e) => onFooterChange({ ...footer, phone: e.target.value })} placeholder="Teléfono"
            className="w-full px-3 py-2 bg-black border border-[#27272A] rounded text-white text-sm focus:border-[#16A34A] outline-none" data-testid="footer-phone-input" />
          <input value={footer.website || ''} onChange={(e) => onFooterChange({ ...footer, website: e.target.value })} placeholder="Sitio web"
            className="w-full px-3 py-2 bg-black border border-[#27272A] rounded text-white text-sm focus:border-[#16A34A] outline-none" data-testid="footer-website-input" />
          <input value={footer.social || ''} onChange={(e) => onFooterChange({ ...footer, social: e.target.value })} placeholder="Redes sociales"
            className="w-full px-3 py-2 bg-black border border-[#27272A] rounded text-white text-sm focus:border-[#16A34A] outline-none" data-testid="footer-social-input" />
          <textarea value={footer.message || ''} onChange={(e) => onFooterChange({ ...footer, message: e.target.value })} rows={2}
            placeholder="Mensaje personalizado (ej: Gracias por su visita)"
            className="w-full px-3 py-2 bg-black border border-[#27272A] rounded text-white text-sm focus:border-[#16A34A] outline-none" data-testid="footer-message-input" />
        </div>
      </details>
      <div className="bg-[#1C1C1E] border border-[#27272A] rounded-lg p-3">
        <label className="text-[#A1A1AA] text-xs uppercase tracking-wide block mb-2">Modo del código</label>
        <div className="grid grid-cols-3 gap-2">
          {[['barcode', 'Barcode'], ['qr', 'QR Code'], ['hidden', 'Oculto']].map(([v, l]) => (
            <button key={v} onClick={() => onBarcodeModeChange(v)}
              className={`py-2 rounded border text-sm font-medium ${barcodeMode === v ? 'bg-[#052E16] border-[#16A34A] text-[#4ADE80]' : 'bg-black border-[#27272A] text-[#A1A1AA]'}`}
              data-testid={`barcode-mode-${v}`}>{l}</button>
          ))}
        </div>
      </div>
    </div>
  );
}

function LivePreview({ template, kind }) {
  const iframeRef = useRef(null);

  useEffect(() => {
    if (!iframeRef.current || !template) return;
    const sample = kind === 'entry' ? SAMPLE_ENTRY : SAMPLE_EXIT;
    // Pre-render barcode SVG in a hidden container so we can serialize it
    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    let barcodeMarkup = '';
    try {
      JsBarcode(svg, sample.ticket_id, { format: 'CODE128', width: 2, height: 50, displayValue: false, margin: 4 });
      svg.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
      svg.setAttribute('style', 'max-width:100%;height:50px');
      barcodeMarkup = svg.outerHTML;
    } catch { barcodeMarkup = ''; }

    const html = buildTicketPrintHTML(template, sample, barcodeMarkup);
    // Strip the auto-print script so preview doesn't trigger the browser dialog
    const previewHtml = html.replace(/<script>[\s\S]*?<\/script>/g, '').replace(/<button class="print-btn[\s\S]*?<\/button>/g, '');
    const doc = iframeRef.current.contentDocument;
    if (doc) {
      doc.open();
      doc.write(previewHtml);
      doc.close();
    }
  }, [template, kind]);

  return (
    <div className="bg-[#1C1C1E] border border-[#27272A] rounded-lg p-3" data-testid="live-preview">
      <div className="text-[#A1A1AA] text-xs uppercase tracking-wide mb-2">Vista previa en vivo</div>
      <div className="bg-white rounded overflow-hidden mx-auto" style={{ maxWidth: 300 }}>
        <iframe ref={iframeRef} title="preview" style={{ width: '100%', height: 500, border: 'none', background: '#fff' }} />
      </div>
    </div>
  );
}

function VersionsPanel({ kind, onRestore }) {
  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await templatesApi.listVersions(kind);
      setVersions(res.data || []);
    } finally { setLoading(false); }
  }, [kind]);

  useEffect(() => { load(); }, [load]);

  const del = async (id) => {
    if (!window.confirm('¿Eliminar esta versión guardada?')) return;
    try { await templatesApi.deleteVersion(kind, id); await load(); } catch (e) { window.alert(formatApiError(e)); }
  };
  const restore = async (id) => {
    if (!window.confirm('¿Restaurar esta versión? La versión actual se guardará en el historial.')) return;
    try {
      const res = await templatesApi.restoreVersion(kind, id);
      onRestore(res.data);
      await load();
    } catch (e) { window.alert(formatApiError(e)); }
  };

  return (
    <div className="bg-[#1C1C1E] border border-[#27272A] rounded-lg p-3" data-testid="versions-panel">
      <div className="flex items-center justify-between mb-2">
        <div className="text-white text-sm font-medium flex items-center gap-1"><ClockCounterClockwise className="w-4 h-4" weight="bold" /> Historial ({versions.length})</div>
        <button onClick={load} disabled={loading} className="text-xs text-[#A1A1AA] hover:text-white">Refrescar</button>
      </div>
      {versions.length === 0 ? (
        <p className="text-[#71717A] text-xs text-center py-3">Sin versiones anteriores</p>
      ) : (
        <div className="space-y-1.5 max-h-56 overflow-y-auto">
          {versions.map((v) => (
            <div key={v.id} className="flex items-center gap-2 p-2 bg-black rounded text-xs">
              <div className="flex-1 min-w-0">
                <div className="text-white font-medium">v{v.version}</div>
                <div className="text-[#71717A] font-mono text-[10px]">{new Date(v.saved_at).toLocaleString('es-CL')} · {v.saved_by}</div>
              </div>
              <button onClick={() => restore(v.id)} className="px-2 py-1 bg-[#D97706] hover:bg-[#F59E0B] rounded text-white font-medium" data-testid={`version-restore-${v.version}`}>Restaurar</button>
              <button onClick={() => del(v.id)} className="p-1 text-[#F87171] hover:text-[#DC2626]" data-testid={`version-delete-${v.version}`}><Trash className="w-3.5 h-3.5" weight="bold" /></button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function AdminTicketDesigner() {
  const [kind, setKind] = useState('entry');
  const [template, setTemplate] = useState(null);
  const [selectedBlockId, setSelectedBlockId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [banner, setBanner] = useState(null); // { type: 'success'|'error', text }
  const [addBlockOpen, setAddBlockOpen] = useState(false);
  const bannerTimerRef = useRef(null);

  const load = useCallback(async (k) => {
    try {
      const res = await templatesApi.get(k);
      setTemplate(res.data);
      if (res.data.blocks?.length && !selectedBlockId) setSelectedBlockId(res.data.blocks[0].id);
    } catch (e) {
      setBanner({ type: 'error', text: formatApiError(e) });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(kind); setSelectedBlockId(null); }, [kind, load]);

  const flash = (type, text) => {
    setBanner({ type, text });
    if (bannerTimerRef.current) clearTimeout(bannerTimerRef.current);
    bannerTimerRef.current = setTimeout(() => setBanner(null), 4000);
  };

  const selectedBlock = useMemo(() => template?.blocks?.find((b) => b.id === selectedBlockId) || null, [template, selectedBlockId]);

  const updateBlock = (updated) => {
    if (!template) return;
    setTemplate({ ...template, blocks: template.blocks.map((b) => (b.id === updated.id ? updated : b)) });
  };

  const moveBlock = (id, delta) => {
    if (!template) return;
    const idx = template.blocks.findIndex((b) => b.id === id);
    if (idx < 0) return;
    const target = idx + delta;
    if (target < 0 || target >= template.blocks.length) return;
    const blocks = [...template.blocks];
    [blocks[idx], blocks[target]] = [blocks[target], blocks[idx]];
    setTemplate({ ...template, blocks });
  };

  const toggleVisible = (id) => {
    if (!template) return;
    setTemplate({ ...template, blocks: template.blocks.map((b) => (b.id === id ? { ...b, visible: !b.visible } : b)) });
  };

  const removeBlock = (id) => {
    if (!template) return;
    if (!window.confirm('¿Eliminar este bloque de la plantilla?')) return;
    setTemplate({ ...template, blocks: template.blocks.filter((b) => b.id !== id) });
    if (selectedBlockId === id) setSelectedBlockId(null);
  };

  const addBlock = (type) => {
    if (!template) return;
    const newBlock = { id: uid(), type, label: BLOCK_TYPE_LABELS[type].toUpperCase().slice(0, 12), size: 'small', style: 'normal', margin_top: 2, margin_bottom: 2, divider: 'none', visible: true, highlight: false };
    setTemplate({ ...template, blocks: [...template.blocks, newBlock] });
    setSelectedBlockId(newBlock.id);
    setAddBlockOpen(false);
  };

  const save = async () => {
    if (!template) return;
    setSaving(true);
    try {
      // Strip response-only fields before PUT
      const payload = { kind: template.kind, name: template.name, header: template.header, footer: template.footer, blocks: template.blocks, barcode_mode: template.barcode_mode };
      const res = await templatesApi.save(kind, payload);
      setTemplate(res.data);
      flash('success', `Plantilla guardada (v${res.data.version})`);
    } catch (e) {
      flash('error', formatApiError(e));
    } finally { setSaving(false); }
  };

  const restoreDefault = async () => {
    if (!window.confirm('¿Restaurar la plantilla predeterminada de fábrica? La actual quedará guardada en el historial.')) return;
    try {
      const res = await templatesApi.restoreDefault(kind);
      setTemplate(res.data);
      flash('success', 'Plantilla restaurada a la predeterminada');
    } catch (e) { flash('error', formatApiError(e)); }
  };

  const testPrint = () => {
    if (!template) return;
    // Sample ticket + local barcode SVG
    const sample = kind === 'entry' ? SAMPLE_ENTRY : SAMPLE_EXIT;
    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    let barcodeMarkup = '';
    try {
      JsBarcode(svg, sample.ticket_id, { format: 'CODE128', width: 2, height: 50, displayValue: false, margin: 4 });
      svg.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
      barcodeMarkup = svg.outerHTML;
    } catch { barcodeMarkup = ''; }

    const html = buildTicketPrintHTML(template, sample, barcodeMarkup);
    // Use the SAME window.open primary path as production printing (delivery mechanism unchanged)
    const pw = window.open('', '_blank');
    if (pw) { pw.document.write(html); pw.document.close(); return; }
    // If popup blocked, use hidden iframe (same fallback pattern as production)
    const iframe = document.createElement('iframe');
    iframe.style.cssText = 'position:fixed;top:-9999px;left:-9999px;width:80mm;height:0;border:none;';
    document.body.appendChild(iframe);
    const doc = iframe.contentDocument;
    doc.open(); doc.write(html); doc.close();
    setTimeout(() => { try { iframe.contentWindow.print(); } catch {} setTimeout(() => { try { document.body.removeChild(iframe); } catch {} }, 3000); }, 600);
  };

  return (
    <div className="space-y-4" data-testid="ticket-designer-panel">
      {/* Header */}
      <div className="bg-gradient-to-br from-[#1C1C1E] to-[#141414] border border-[#27272A] rounded-2xl p-4">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h2 className="text-white font-bold text-lg">Plantillas de ticket</h2>
            <p className="text-[#A1A1AA] text-xs">Diseñe tickets de entrada y salida. Los cambios se aplican a los tickets futuros; el historial no se modifica.</p>
          </div>
          <div className="flex gap-2 flex-wrap">
            <button onClick={testPrint} className="px-3 py-2 bg-[#1D4ED8] hover:bg-[#2563EB] rounded-lg text-white text-sm font-medium flex items-center gap-1" data-testid="test-print-btn">
              <Printer className="w-4 h-4" weight="bold" /> Test print
            </button>
            <button onClick={save} disabled={saving} className="px-3 py-2 bg-[#15803D] hover:bg-[#16A34A] disabled:opacity-50 rounded-lg text-white text-sm font-medium flex items-center gap-1" data-testid="save-template-btn">
              <FloppyDisk className="w-4 h-4" weight="bold" /> {saving ? 'Guardando...' : 'Guardar'}
            </button>
            <button onClick={restoreDefault} className="px-3 py-2 bg-[#7F1D1D] hover:bg-[#B91C1C] rounded-lg text-white text-sm font-medium flex items-center gap-1" data-testid="restore-default-btn">
              <ArrowCounterClockwise className="w-4 h-4" weight="bold" /> Predeterminada
            </button>
          </div>
        </div>
        <div className="flex gap-2 mt-3">
          <button onClick={() => setKind('entry')} className={`px-4 py-1.5 rounded font-medium text-sm ${kind === 'entry' ? 'bg-[#16A34A] text-white' : 'bg-[#27272A] text-[#A1A1AA]'}`} data-testid="kind-entry-btn">Ticket de entrada</button>
          <button onClick={() => setKind('exit')} className={`px-4 py-1.5 rounded font-medium text-sm ${kind === 'exit' ? 'bg-[#16A34A] text-white' : 'bg-[#27272A] text-[#A1A1AA]'}`} data-testid="kind-exit-btn">Ticket de salida</button>
          {template && <span className="ml-auto text-[#71717A] text-xs self-center">v{template.version} · {template.updated_by}</span>}
        </div>
        {banner && (
          <div className={`mt-3 p-2 rounded text-sm ${banner.type === 'success' ? 'bg-[#052E16] text-[#4ADE80]' : 'bg-[#450A0A] text-[#FCA5A5]'}`} data-testid="designer-banner">{banner.text}</div>
        )}
      </div>

      {template && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Column 1: Blocks list */}
          <div className="space-y-3">
            <div className="bg-[#141414] border border-[#27272A] rounded-2xl p-3">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-white text-sm font-bold uppercase tracking-wider">Bloques ({template.blocks.length})</h3>
                <button onClick={() => setAddBlockOpen((v) => !v)} className="p-1 bg-[#27272A] hover:bg-[#3F3F46] rounded" data-testid="add-block-btn">
                  <Plus className="w-4 h-4 text-white" weight="bold" />
                </button>
              </div>
              {addBlockOpen && (
                <div className="mb-2 p-2 bg-[#1C1C1E] border border-[#27272A] rounded-lg" data-testid="add-block-menu">
                  <div className="grid grid-cols-2 gap-1 max-h-48 overflow-y-auto">
                    {AVAILABLE_BLOCK_TYPES.map((t) => (
                      <button key={t} onClick={() => addBlock(t)} className="p-1.5 text-xs bg-black hover:bg-[#27272A] rounded text-white text-left" data-testid={`add-block-${t}`}>
                        {BLOCK_TYPE_LABELS[t]}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              <div className="space-y-1.5 max-h-[500px] overflow-y-auto" data-testid="blocks-list">
                {template.blocks.map((b, idx) => (
                  <BlockRow
                    key={b.id}
                    block={b}
                    selected={b.id === selectedBlockId}
                    onSelect={() => setSelectedBlockId(b.id)}
                    onMoveUp={() => moveBlock(b.id, -1)}
                    onMoveDown={() => moveBlock(b.id, 1)}
                    onToggleVisible={() => toggleVisible(b.id)}
                    onRemove={() => removeBlock(b.id)}
                    canUp={idx > 0}
                    canDown={idx < template.blocks.length - 1}
                  />
                ))}
              </div>
            </div>
          </div>

          {/* Column 2: Properties + Header/Footer + Versions */}
          <div className="space-y-3">
            <div className="bg-[#141414] border border-[#27272A] rounded-2xl p-3">
              <h3 className="text-white text-sm font-bold uppercase tracking-wider mb-2">Propiedades del bloque</h3>
              <BlockProperties block={selectedBlock} onChange={updateBlock} />
            </div>
            <div className="bg-[#141414] border border-[#27272A] rounded-2xl p-3">
              <h3 className="text-white text-sm font-bold uppercase tracking-wider mb-2">Cabecera, pie y código</h3>
              <HeaderFooterEditor
                header={template.header || {}}
                footer={template.footer || {}}
                barcodeMode={template.barcode_mode || 'barcode'}
                onHeaderChange={(h) => setTemplate({ ...template, header: h })}
                onFooterChange={(f) => setTemplate({ ...template, footer: f })}
                onBarcodeModeChange={(m) => setTemplate({ ...template, barcode_mode: m })}
              />
            </div>
            <VersionsPanel kind={kind} onRestore={setTemplate} />
          </div>

          {/* Column 3: Live preview */}
          <div className="lg:sticky lg:top-4 self-start">
            <LivePreview template={template} kind={kind} />
          </div>
        </div>
      )}
    </div>
  );
}
